import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TeacherFilter from '../shared/teacher_filter';
import { fetch_email_outbox, fetch_email_switch } from '../api';
import { OUTBOX_KINDS, OUTBOX_STATUSES, outbox_view, read_error } from './adapters';
import SwitchCard from './switch_card';
import '../payment_details/payment_details.css';
import './payment_emails.css';

/*
 * PROD-7828 A-22 W6 — the Payment emails tab (/teacher-payments/payment-emails): the "Send payment
 * emails to teachers" switch and the payment email history (newest first, paged by before_id, filtered
 * by teacher, status and email type, e.g. "Invoice reminder"). Never the email body or a payment detail.
 * props: on_error(e, fallback) (the shell: a 401/403 means no module grant), on_notify(type, message).
 */
const OUTBOX_FAILED_TEXT = 'Could not load the payment email history.';
const SWITCH_FAILED_TEXT = 'Could not read the payment email switch.';
const NO_TEACHER = { teacher_uuid: '', display: '' };

const COLUMNS = [
  {
    key: 'created_at',
    label: 'Queued (IST)',
    render: (r) => (
      <div>
        <div>{r.created_at}</div>
        {r.sent_at ? <div className="tp-hint">Sent {r.sent_at}</div> : null}
      </div>
    )
  },
  {
    key: 'teacher',
    label: 'Teacher',
    hideInCard: true,
    render: (r) => r.teacher_name
  },
  {
    key: 'kind',
    label: 'Email',
    render: (r) => (
      <div>
        <div>{r.kind_label}</div>
        {r.pay_month ? <div className="tp-hint">Month {r.pay_month}</div> : null}
        {r.subject ? <div className="tp-hint">{r.subject}</div> : null}
      </div>
    )
  },
  {
    key: 'status',
    label: 'Status',
    render: (r) => (
      <div className="tp-pe-status">
        <StatusPill tone={r.status_tone}>{r.status_label}</StatusPill>
        <div className="tp-hint">{r.status_help}</div>
        {r.failure_reason ? <div className="tp-neg">{r.failure_reason}</div> : null}
        {r.delivery_problem ? <div className="tp-neg">{r.delivery_problem}</div> : null}
        {r.next_attempt_at ? <div className="tp-hint">Next try {r.next_attempt_at} IST</div> : null}
        {r.attempts > 1 ? <div className="tp-hint">{r.attempts} attempts</div> : null}
      </div>
    )
  },
  {
    key: 'to',
    label: 'Sent to',
    render: (r) => (
      <div>
        <div>{r.to.length ? r.to.join(', ') : '—'}</div>
        {r.cc.length ? <div className="tp-hint">Copy: {r.cc.join(', ')}</div> : null}
      </div>
    )
  }
];

const PaymentEmailsTab = ({ on_error, on_notify }) => {
  const [switch_data, setSwitchData] = useState(null);
  const [switch_error, setSwitchError] = useState('');
  const [teacher, setTeacher] = useState(NO_TEACHER);
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [list, setList] = useState({ state: 'loading', rows: [], next: null });
  const [more_busy, setMoreBusy] = useState(false);
  const [list_error, setListError] = useState('');
  const switch_seq = useRef(0);
  const list_seq = useRef(0);

  const load_switch = useCallback(() => {
    switch_seq.current += 1;
    const mine = switch_seq.current;
    setSwitchError('');
    fetch_email_switch()
      .then((d) => {
        if (mine === switch_seq.current) setSwitchData(d);
      })
      .catch((e) => {
        if (mine !== switch_seq.current) return;
        const v = read_error(e, SWITCH_FAILED_TEXT);
        if (v.kind === 'permission') on_error(e, SWITCH_FAILED_TEXT);
        else setSwitchError(v.text);
      });
  }, [on_error]);

  const load_list = useCallback(
    (before_id) => {
      list_seq.current += 1;
      const mine = list_seq.current;
      if (before_id) setMoreBusy(true);
      else setList((l) => ({ ...l, state: 'loading' }));
      setListError('');
      fetch_email_outbox({ teacher_uuid: teacher.teacher_uuid, status, kind }, before_id)
        .then((d) => {
          if (mine !== list_seq.current) return;
          const v = outbox_view(d);
          setMoreBusy(false);
          setList((l) => ({
            state: 'ready',
            rows: before_id ? l.rows.concat(v.rows) : v.rows,
            next: v.next_before_id
          }));
        })
        .catch((e) => {
          if (mine !== list_seq.current) return;
          setMoreBusy(false);
          const v = read_error(e, OUTBOX_FAILED_TEXT);
          if (v.kind === 'permission') {
            on_error(e, OUTBOX_FAILED_TEXT);
            return;
          }
          setListError(v.text);
          // A failed "Older emails" keeps the rows already shown.
          if (!before_id) setList({ state: 'error', rows: [], next: null });
        });
    },
    [teacher.teacher_uuid, status, kind, on_error]
  );

  useEffect(() => {
    load_switch();
  }, [load_switch]);

  useEffect(() => {
    load_list(null);
  }, [load_list]);

  const on_switch_changed = (d) => {
    setSwitchData(d);
    if (d && typeof d.live_enabled === 'boolean') {
      on_notify('success', `Sending to teachers is now ${d.live_enabled ? 'ON' : 'OFF'}.`);
    }
  };

  return (
    <div className="tp-pe-tab">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" role="heading" aria-level={2}>
          Payment emails
        </span>
        <span className="tp-card-hd-sub">
          Every payment email to a teacher, newest first, and where they are sent.
        </span>
      </div>

      <SwitchCard
        data={switch_data}
        load_error={switch_error}
        on_retry={load_switch}
        on_changed={on_switch_changed}
        on_stale={load_switch}
      />

      <div className="tp-frow">
        <TeacherFilter value={teacher} onChange={setTeacher} />
        <select
          className="tp-select"
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {OUTBOX_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          aria-label="Filter by email type"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">All emails</option>
          {OUTBOX_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </div>

      {list.state === 'loading' ? <CustomLoading /> : null}
      {list.state === 'error' ? (
        <LoadFailed text={list_error} on_retry={() => load_list(null)} />
      ) : null}
      {list.state === 'ready' ? (
        <>
          <DataTable
            columns={COLUMNS}
            rows={list.rows}
            rowKey={(r) => r.uuid || String(r.id)}
            emptyText={
              teacher.teacher_uuid || status || kind
                ? 'No payment emails match these filters.'
                : 'No payment emails yet.'
            }
            cardTitle={(r) => <b>{r.teacher_name}</b>}
          />
          {list_error ? (
            <LoadFailed text={list_error} on_retry={() => load_list(list.next)} />
          ) : null}
          {list.next ? (
            <div className="tp-pd-more">
              <button
                type="button"
                className="tp-btn-out"
                onClick={more_busy ? undefined : () => load_list(list.next)}
                aria-disabled={more_busy ? 'true' : undefined}
              >
                {more_busy ? 'Loading…' : 'Older emails'}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default PaymentEmailsTab;
